/**
 * Query params that carry a bearer credential and must never reach
 * Sentry (breadcrumbs / request urls / transaction names). `wlt` is the
 * waitlist magic-link token; `token` covers any other one-time link.
 */
const SENSITIVE_QUERY_PARAMS = ['wlt', 'token'] as const

/**
 * Matches only the targeted param values — other url parts are
 * untouched. Works for relative urls (unlike `new URL()`, which throws
 * on them).
 */
const sensitiveParamPattern = new RegExp(
  `([?&](?:${SENSITIVE_QUERY_PARAMS.join('|')})=)[^&#]*`,
  'gi',
)

/**
 * Redact the values of sensitive query params (`wlt`, `token`) in a url,
 * leaving everything else untouched. Returns the input unchanged when it
 * is empty or carries none of the params. Safe for relative urls — never
 * throws the way `new URL(relativePath)` would.
 */
export const redactSensitiveQueryParams = (url?: string | null): string =>
  url ? url.replace(sensitiveParamPattern, '$1REDACTED') : (url ?? '')

/**
 * Error signatures injected by software we don't ship — in-app
 * browsers' webview bridges, browser extensions, and
 * link-scanner bots. None of these symbols exist in our source, so
 * dropping them cannot hide a real app error. Fed to Sentry's
 * `ignoreErrors`, which (unlike `beforeSend`) also matches the
 * bare-string promise rejections these sources emit.
 */
export const THIRD_PARTY_NOISE_PATTERNS = [
  // extension / native-bridge string rejection
  /Object Not Found Matching Id/,
  // iOS in-app webview bridge (Instagram et al)
  /window\.webkit\.messageHandlers/,
  // Snapchat's in-app browser, referencing its bridge after teardown
  /SCDynimacBridge/,
  // Android WebView bridge object GC'd mid-call
  /Java object is gone/,
  // Android WebView bridge: host's Java method threw
  /Java exception was raised during method invocation/,
  // iOS host bridge (DuckDuckGo) never sent a reply
  /WKWebView API client did not respond/,
  // WebExtension content-script messaging
  /Invalid call to runtime\.sendMessage/,
]

/**
 * A request that died before any response arrived — the server never
 * answered (flaky mobile wifi, user navigated away, aborted request).
 * Matches both the raw axios error shape (`isAxiosError`, no `response`)
 * and the app's wrapped api error shape (`isNetworkError` from
 * `describeApiError`). These are client connectivity, not code defects,
 * so `beforeSend` drops them instead of alarming Sentry.
 */
export const isClientNetworkError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false
  if ('response' in err && err.response) return false
  if ('isAxiosError' in err && err.isAxiosError === true) return true
  return 'isNetworkError' in err && err.isNetworkError === true
}

/**
 * WebKit drops Firebase Auth's IndexedDB connection under memory
 * pressure / backgrounding on iOS Safari; the SDK surfaces it as an
 * unhandled rejection and recovers by itself (no auth loss, no
 * crash). Matches both the raw DOMException and Firebase's wrapped
 * Error, keyed on WebKit's exact message so an app-owned IndexedDB
 * failure (different message) still reports.
 */
export const isTransientIndexedDbError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'message' in err &&
  typeof err.message === 'string' &&
  err.message.includes('Connection to Indexed Database server lost')

/**
 * Firebase Auth's own connectivity failure — the SDK could not reach
 * Identity Platform. Its background token refresh raises this as an
 * unhandled rejection with no app frame in the stack, so there is
 * nothing to fix. Same class as `isClientNetworkError`; the login form
 * still shows its own copy through `getAuthErrorMessage`.
 */
export const isFirebaseNetworkError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  'code' in err &&
  err.code === 'auth/network-request-failed'

/**
 * A request the browser threw away — the user navigated mid-flight, or
 * iOS suspended a backgrounded tab. Arrives as a DOMException named
 * AbortError, or wrapped by the SDK with the name in the message. Our
 * own timeouts abort through axios (`ECONNABORTED`), which
 * `isClientNetworkError` already covers, so nothing app-owned is lost.
 */
export const isClientAbortError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false
  if ('name' in err && err.name === 'AbortError') return true
  return (
    'message' in err &&
    typeof err.message === 'string' &&
    /^(Error: )?AbortError\b/.test(err.message)
  )
}

const CLIENT_NOISE_PREDICATES = [
  isClientNetworkError,
  isTransientIndexedDbError,
  isFirebaseNetworkError,
  isClientAbortError,
]

/**
 * The browser's condition, not our defect — a dropped connection, an
 * aborted request, a platform storage blip. `beforeSend` drops these so
 * a mobile signal drop never reads as a production error.
 */
export const isClientNoise = (err: unknown): boolean =>
  CLIENT_NOISE_PREDICATES.some((matches) => matches(err))
